import apiClient from './apiClient';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    teamIds: string[];
    requiresPasswordChange?: boolean;
  };
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshTokenResponse {
  accessToken: string;
  expiresIn: number;
}

export interface ForgotPasswordResponse {
  message?: string;
}

export const authService = {
  /**
   * Login user
   */
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response: any = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  /**
   * Refresh access token
   */
  refreshToken: async (refreshToken: string): Promise<RefreshTokenResponse> => {
    const response: any = await apiClient.post('/auth/refresh', { refreshToken });
    return response.data;
  },

  /**
   * Request password reset instructions.
   */
  forgotPassword: async (email: string): Promise<ForgotPasswordResponse> => {
    const response: any = await apiClient.post('/auth/forgot-password', { email });
    return { message: response.message };
  },

  /**
   * Complete password reset.
   */
  resetPassword: async (token: string, password: string): Promise<void> => {
    await apiClient.post('/auth/reset-password', { token, password });
  },

  /**
   * Change the password for the currently signed-in user.
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/change-password', { currentPassword, newPassword });
  },

  /**
   * Get current user
   */
  getCurrentUser: async (): Promise<LoginResponse['user']> => {
    const response: any = await apiClient.get('/auth/me');
    return response.data;
  },
};
